import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { useUserProfile } from "api/services/useUserProfile";
import { useUser } from "hooks/user/useUser";
import { Typography } from "new-components/typography/Typography";

import { getStepsContent } from "./utils";

interface ContentProps {
  title: string;
  badge?: string;
  children: React.ReactNode;
}

function Content(props: ContentProps) {
  const { title, children, badge } = props;

  return (
    <Box className="flex flex-col gap-2 min-h-[135px]">
      <Stack direction="row" spacing={1}>
        <Typography variant="main" fontWeight="semibold" className="text-black">
          {title}
        </Typography>
        {badge && <Chip label={badge} className="bg-primary-light text-white text-base font-semibold" />}
      </Stack>
      <Typography className="text-black">
        {children}
      </Typography>
    </Box>
  );
}

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OnboardingModal(props: OnboardingModalProps) {
  const { isOpen, onClose } = props;

  const user = useUser();
  const stepsContent = getStepsContent(user.tier);
  const MAX_STEPS = stepsContent.length;
  const [currentStep, setCurrentStep] = useState<number>(1);
  const { updateUserProfile } = useUserProfile();

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const handleBackStep = () => setCurrentStep((step) => step - 1);

  const handleClose = () => {
    onClose();
    updateUserProfile({
      detector_management_onboarded: true,
    });
  };

  const handleConfirmation = () => {
    updateUserProfile({
      detector_management_onboarded: true,
    });
  };

  const handleNextStep = () => {
    if (currentStep === MAX_STEPS) {
      handleConfirmation();
      onClose();
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  const stepContent = stepsContent[currentStep - 1];

  return (
    <Dialog
      open={isOpen}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      PaperProps={{
        sx: {
          maxWidth: "665.54px",
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle className="p-6 bg-[#CCEAFF]">
        <Box className="w-full flex justify-end">
          <IconButton onClick={handleClose} className="p-1">
            <CloseIcon className="text-gray-600" />
          </IconButton>
        </Box>
        <Box className="flex mx-auto justify-center items-center min-h-[270px] max-w-[506px]">{stepContent.image}</Box>
      </DialogTitle>

      <DialogContent className="p-6 px-10">
        <Content title={stepContent.title} badge={stepContent.badge}>
          {stepContent.content}
        </Content>
      </DialogContent>

      <DialogActions className="flex justify-between py-6 px-10">
        <Typography className="text-[#C8CDD3]">
          Step ({currentStep}/{MAX_STEPS})
        </Typography>

        <Box className="flex justify-end gap-2">
          {currentStep !== 1 && (
            <Button className="rounded-3xl normal-case" onClick={handleBackStep}>
              Back
            </Button>
          )}

          <Button onClick={handleNextStep} className="rounded-3xl bg-[#0450BA] text-white min-w-[200px] normal-case">
            {currentStep === MAX_STEPS ? "Finish" : "Next"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
